/**
 * KAN-852: одно правило "какие действия взятия в работу есть у пользователя".
 * Взять в работу = забрать клиента у бота: строка уходит из "Требуют внимания"
 * (POST dashboard/attention/<id>/done/), бот больше не пишет первым (KAN-769),
 * клиент закрепляется за менеджером. Это не итог сделки (итоги: Успех, Отказ).
 *   - обычный менеджер (кабинет, приложение, бот): "Взять в работу" себе;
 *   - старший менеджер: в кабинете "Назначить в работу" (выбор менеджера),
 *     в приложении "Взять в работу" себе и "Назначить";
 *   - владелец и оператор: "Назначить в работу" (выбор менеджера).
 * Права на конкретного клиента (чужого обычный менеджер не берёт) решает
 * clientManagerRights; здесь только набор действий по роли.
 */
import { isManagerUser } from './managerAccess';

export const TAKE_TO_WORK_LABEL = 'Взять в работу';
export const ASSIGN_TO_WORK_LABEL = 'Назначить в работу';
/** Вторичное действие старшего менеджера в приложении. */
export const ASSIGN_SHORT_LABEL = 'Назначить';
export const TAKEN_TO_WORK_TEXT = 'Взято в работу: бот больше не пишет первым';
export const ASSIGNED_TO_WORK_TEXT = 'Назначено в работу: бот больше не пишет первым';
/** KAN-880: одна кнопка итога вместо Успех/Отказ и её выбор. */
export const WORK_DONE_LABEL = 'Отработано';
export const WORK_SUCCESS_LABEL = 'Успех';
export const WORK_FAILURE_LABEL = 'Не успех';

/** KAN-880: атрибут корня кнопки "Отработано", пока выбор итога раскрыт. */
export const WORK_OUTCOME_OPEN_ATTR = 'data-work-outcome-open';

/** KAN-880: событие пришло из раскрытого выбора "Отработано" (Esc закрывает выбор, а не панель). */
export function isInsideOpenWorkOutcome(target: EventTarget | null): boolean {
  return target instanceof Element && !!target.closest(`[${WORK_OUTCOME_OPEN_ATTR}]`);
}

/** Где показываются действия: кабинет или личное приложение менеджера (/m). */
export type WorkSurface = 'cabinet' | 'app';

export interface WorkActions {
  /** "Взять в работу" себе. */
  takeSelf: boolean;
  /** "Назначить в работу" с выбором менеджера. */
  assign: boolean;
  /** Запись справочника текущего пользователя-менеджера; null у остальных. */
  selfId: string | null;
}

interface WorkUser {
  role?: string;
  manager_id?: string | null;
  is_senior_manager?: boolean;
}

/** Никаких действий: в приложении, пока неизвестно, кто вошёл. */
const NO_ACTIONS: WorkActions = { takeSelf: false, assign: false, selfId: null };

export function workActions(user: WorkUser | null | undefined, surface: WorkSurface): WorkActions {
  // KAN-856: приложение (/m) открывают в основном менеджеры: без /auth/me
  // (ошибка запроса) кнопки владельца "Назначить в работу" не показываются.
  if (!user && surface === 'app') return NO_ACTIONS;
  const selfId = user?.manager_id ?? null;
  if (!isManagerUser(user)) return { takeSelf: false, assign: true, selfId: null };
  if (!user?.is_senior_manager) return { takeSelf: true, assign: false, selfId };
  if (surface === 'cabinet') return { takeSelf: false, assign: true, selfId };
  return { takeSelf: selfId !== null, assign: true, selfId };
}

/** Подпись основной кнопки: "Взять в работу" или "Назначить в работу". */
export function primaryWorkLabel(actions: WorkActions): string {
  return actions.takeSelf ? TAKE_TO_WORK_LABEL : ASSIGN_TO_WORK_LABEL;
}
